'use client';
import React from 'react';
import { Search, Truck, Wrench, User, Wifi, WifiOff, AlertTriangle, Map } from 'lucide-react';
import StatusBadge from '@/components/ui/StatusBadge';
import type { Asset } from './LiveTrackingView';
import Icon from '@/components/ui/AppIcon';


interface AssetListPanelProps {
  assets: Asset[];
  selectedAssetId: string | null;
  onSelectAsset: (id: string) => void;
  onOpenMap: (id: string) => void;
  filterType: 'all' | 'vehicle' | 'equipment' | 'person';
  setFilterType: (v: 'all' | 'vehicle' | 'equipment' | 'person') => void;
  filterStatus: string;
  setFilterStatus: (v: string) => void;
  searchQuery: string;
  setSearchQuery: (v: string) => void;
}

const typeIcon = {
  vehicle: Truck,
  equipment: Wrench,
  person: User,
};

const typeFilters: { key: 'all' | 'vehicle' | 'equipment' | 'person'; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'vehicle', label: 'Vehicles' },
  { key: 'equipment', label: 'Equipment' },
  { key: 'person', label: 'People' },
];

export default function AssetListPanel({
  assets,
  selectedAssetId,
  onSelectAsset,
  onOpenMap,
  filterType,
  setFilterType,
  searchQuery,
  setSearchQuery,
}: AssetListPanelProps) {
  const filtered = assets.filter((a) => {
    const matchType = filterType === 'all' || a.type === filterType;
    const q = searchQuery.toLowerCase();
    const matchSearch =
      !q ||
      a.unitNumber.toLowerCase().includes(q) ||
      (a.driver ?? '').toLowerCase().includes(q) ||
      (a.jobSite ?? '').toLowerCase().includes(q);
    return matchType && matchSearch;
  });

  return (
    <div className="w-72 flex-shrink-0 bg-zinc-900 border border-zinc-800 rounded-xl flex flex-col overflow-hidden">
      {/* Search */}
      <div className="p-3 border-b border-zinc-800">
        <div className="relative">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input
            type="text"
            placeholder="Search assets..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full bg-zinc-800 border border-zinc-700 rounded-md pl-7 pr-3 py-1.5 text-xs text-zinc-300 placeholder-zinc-600 focus:outline-none focus:border-amber-500/50 transition-all"
          />
        </div>
        {/* Type filter pills */}
        <div className="flex gap-1 mt-2">
          {typeFilters.map((f) => (
            <button
              key={`type-filter-${f.key}`}
              onClick={() => setFilterType(f.key)}
              className={`flex-1 text-[11px] font-medium py-1 rounded-md transition-all duration-150 ${filterType === f.key ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30' : 'bg-zinc-800 text-zinc-500 hover:text-zinc-300'}`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Count */}
      <div className="px-3 py-2 border-b border-zinc-800/50">
        <p className="text-[10px] text-zinc-600 font-medium">{filtered.length} assets</p>
      </div>

      {/* Asset list */}
      <div className="flex-1 overflow-y-auto scrollbar-thin divide-y divide-zinc-800/40">
        {filtered.map((asset) => {
          const Icon = typeIcon[asset.type];
          const isSelected = selectedAssetId === asset.id;
          const hasAlert = asset.alerts.length > 0;
          return (
            <div
              key={asset.id}
              className={`w-full text-left px-3 py-2.5 transition-all duration-150 flex items-start gap-2.5 ${isSelected ? 'bg-amber-500/10 border-l-2 border-amber-500' : 'hover:bg-zinc-800/50 border-l-2 border-transparent'}`}
            >
              {/* Icon — click to select */}
              <button
                onClick={() => onSelectAsset(asset.id)}
                className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 ${isSelected ? 'bg-amber-500/20' : 'bg-zinc-800'}`}
              >
                <Icon size={13} className={isSelected ? 'text-amber-400' : 'text-zinc-400'} />
              </button>

              <div className="flex-1 min-w-0">
                <button onClick={() => onSelectAsset(asset.id)} className="w-full text-left">
                  <div className="flex items-center justify-between gap-1">
                    <span className="text-xs font-semibold text-zinc-200 font-mono">{asset.unitNumber}</span>
                    <div className="flex items-center gap-1">
                      {hasAlert && <AlertTriangle size={10} className="text-red-400" />}
                      {asset.signal === 'lost' ? <WifiOff size={10} className="text-red-400" /> : asset.signal === 'weak' ? <Wifi size={10} className="text-amber-400" /> : <Wifi size={10} className="text-emerald-400" />}
                    </div>
                  </div>
                  {asset.driver && <p className="text-[11px] text-zinc-400 truncate">{asset.driver}</p>}
                  {!asset.driver && <p className="text-[11px] text-zinc-500 truncate">{asset.make} {asset.model}</p>}
                  <div className="flex items-center gap-2 mt-1">
                    <StatusBadge status={asset.status as any} size="sm" />
                    {asset.speed > 0 && (
                      <span className="text-[10px] text-zinc-500 font-mono">{asset.speed} mph</span>
                    )}
                  </div>
                  {asset.jobSite && (
                    <p className="text-[10px] text-zinc-600 truncate mt-0.5">{asset.jobSite}</p>
                  )}
                </button>
              </div>

              {/* Map button */}
              <button
                onClick={() => onOpenMap(asset.id)}
                title="View on map"
                className="flex-shrink-0 w-6 h-6 rounded-md flex items-center justify-center text-zinc-600 hover:text-amber-400 hover:bg-amber-500/10 transition-all mt-0.5"
              >
                <Map size={12} />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}